/* Shared shell used by the platform-wide Contacts index and its scoped
   variants (workspace / agency). All routing / data / actions live here so
   the three routes stay in perfect visual + behavioral sync. */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Search, Plus, Download, MoreVertical, Filter as FilterIcon, ArrowUpDown,
  Settings as Cog, Mail, Phone as PhoneIcon, ChevronDown, ChevronUp, X,
  Pencil, ChevronLeft, ChevronRight, ListFilter, Upload, Merge, ArrowRightLeft,
  Trash2, Tag as TagIcon, RefreshCw, ExternalLink,
} from "lucide-react";
import { ig } from "../inbox/_inbox_shared";
import { api } from "../../../lib/apiClient";
import AddContactModal from "./AddContactModal";

/* ── Public shell (used by ContactsPage + variants) ─────────────── */
export default function ContactsShell({
  scope = "platform",          // "platform" | "workspace" | "agency"
  workspaceId = null,
  agencyId    = null,
  scopeLabel  = null,          // e.g. "Workspace: FundElite HQ"
  scopeHref   = null,          // link back to the parent record
}) {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [query,    setQuery]    = useState(sp.get("q") || "");
  const [page,     setPage]     = useState(Number(sp.get("page") || 1));
  const [pageSize, setPageSize] = useState(Number(sp.get("limit") || 20));
  const [sortKey,  setSortKey]  = useState(sp.get("sort")  || "created_at");
  const [sortDir,  setSortDir]  = useState(sp.get("order") || "desc");
  const [selected, setSelected] = useState(new Set());

  // Filters from URL
  const [filters, setFilters] = useState(() => ({
    workspace_id:  sp.get("workspace_id")  || workspaceId || "",
    agency_id:     sp.get("agency_id")     || agencyId    || "",
    owner_user_id: sp.get("owner_user_id") || "",
    status:        sp.get("status")        || "",
    source:        sp.get("source")        || "",
    tag:           sp.get("tag")           || "",
    plan:          sp.get("plan")          || "",
    created_from:  sp.get("created_from")  || "",
    created_to:    sp.get("created_to")    || "",
  }));

  const [filterMeta, setFilterMeta] = useState({ workspaces: [], agencies: [], owners: [], tags: [], plans: [], statuses: [], sources: [] });
  const [showAdv, setShowAdv]         = useState(false);
  const [showAdd, setShowAdd]         = useState(false);
  const [showImport, setShowImport]   = useState(false);
  const [showReassign, setShowReassign] = useState(false);
  const [showMore, setShowMore]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const qs = new URLSearchParams({
        page, limit: pageSize, sort: sortKey, order: sortDir,
        ...(query ? { q: query } : {}),
        ...Object.fromEntries(Object.entries(filters).filter(([_k, v]) => v)),
      });
      const data = await api.get(`/api/admin/contacts?${qs}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (e) {
      setError(e.message || String(e));
    } finally { setLoading(false); }
  }, [page, pageSize, sortKey, sortDir, query, filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/api/admin/contacts/filters").then(setFilterMeta).catch(() => {});
  }, []);

  // Sync filters → URL
  useEffect(() => {
    if (scope !== "platform") return;
    const next = new URLSearchParams();
    if (query) next.set("q", query);
    if (page > 1) next.set("page", String(page));
    if (pageSize !== 20) next.set("limit", String(pageSize));
    Object.entries(filters).forEach(([k, v]) => { if (v) next.set(k, v); });
    setSp(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, page, pageSize, filters, scope]);

  const filterCount = Object.values(filters).filter(Boolean).length;
  const allChecked  = items.length > 0 && items.every((r) => selected.has(r.id));
  const someChecked = items.some((r) => selected.has(r.id));
  const toggleRow = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allChecked) items.forEach((r) => n.delete(r.id));
    else            items.forEach((r) => n.add(r.id));
    return n;
  });
  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("asc"); }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} contact(s)? This cannot be undone.`)) return;
    await api.post("/api/admin/contacts/bulk-delete", { ids: [...selected] });
    setSelected(new Set());
    await load();
  };
  const doExport = async () => {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, v); });
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/contacts/export?${qs}`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${api.token()}` } });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`Export failed (${res.status}): ${t.slice(0, 120)}`);
      }
      const ct = res.headers.get("content-type") || "";
      if (!ct.includes("text/csv")) {
        throw new Error("Unexpected content type: " + ct);
      }
      const b = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(b); a.download = "contacts.csv"; a.click();
    } catch (e) {
      alert(e.message || "Export failed");
    }
  };

  return (
    <div data-testid="contacts-page" style={{
      minHeight: "calc(100vh - 60px)", background: "#F6F8F7",
      fontFamily: "Inter, system-ui, -apple-system, sans-serif", color: ig.ink,
    }}>
      {/* Header */}
      <div style={{ padding: "20px 24px 12px", display: "flex", alignItems: "center", gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: -0.4 }}>Contacts</h1>
          {scopeLabel && (
            <div style={{ marginTop: 4, fontSize: 12, color: ig.muted, display: "inline-flex", alignItems: "center", gap: 6 }}>
              Filtered scope · {scopeLabel}
              {scopeHref && <Link to={scopeHref} style={{ color: "#2563EB", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 3 }}>Open <ExternalLink size={11} /></Link>}
              <button onClick={() => nav("/admin/contacts")} style={pillBtn} data-testid="clear-scope">Clear scope</button>
            </div>
          )}
        </div>
        <span data-testid="contacts-count-pill" style={{
          background: "#DBEAFE", color: "#1D4ED8", padding: "4px 10px", borderRadius: 999,
          fontSize: 12, fontWeight: 700,
        }}>{total} Contacts</span>
        <div style={{ flex: 1 }} />
        <button data-testid="contacts-import" onClick={() => setShowImport(true)} style={btnGhost}>
          <Upload size={13} /> Import
        </button>
        <button data-testid="contacts-export" onClick={doExport} style={btnGhost}>
          <Download size={13} /> Export
        </button>
        <button data-testid="contacts-add" onClick={() => setShowAdd(true)} style={btnPrimaryGreen}>
          <Plus size={13} /> Add Contact
        </button>
        <button data-testid="contacts-more" onClick={() => setShowMore((v) => !v)} style={iconBtn}>
          <MoreVertical size={15} />
        </button>
        {showMore && (
          <div data-testid="contacts-more-menu" style={moreMenu}>
            <button style={menuItem} onClick={() => { setShowMore(false); setShowReassign(true); }} disabled={selected.size === 0}>
              <ArrowRightLeft size={13} /> Reassign selected ({selected.size})
            </button>
            <button style={menuItem} onClick={async () => {
              setShowMore(false);
              if (selected.size < 2) return alert("Select 2+ contacts to merge");
              const [primary, ...dupes] = [...selected];
              if (!window.confirm(`Merge ${dupes.length} contact(s) into the first selected? This cannot be undone.`)) return;
              await api.post("/api/admin/contacts/merge", { primary_id: primary, duplicate_ids: dupes });
              setSelected(new Set()); await load();
            }} disabled={selected.size < 2}>
              <Merge size={13} /> Merge duplicates
            </button>
            <button style={{ ...menuItem, color: "#B91C1C" }} onClick={() => { setShowMore(false); bulkDelete(); }} disabled={selected.size === 0}>
              <Trash2 size={13} /> Delete selected
            </button>
            <button style={menuItem} onClick={() => { setShowMore(false); load(); }}>
              <RefreshCw size={13} /> Refresh
            </button>
          </div>
        )}
      </div>

      {/* Filter/Search row */}
      <div style={{ padding: "10px 24px", display: "flex", alignItems: "center", gap: 10,
        background: ig.card, borderBottom: `1px solid ${ig.border}`, flexWrap: "wrap" }}>
        <button data-testid="adv-filters-open" onClick={() => setShowAdv(true)} style={btnGhost}>
          <FilterIcon size={13} /> Advanced filters
          {filterCount > 0 && <span style={{ background: "#DCFCE7", color: "#15803D", borderRadius: 999,
            padding: "1px 6px", fontSize: 10.5, fontWeight: 800, marginLeft: 4 }}>{filterCount}</span>}
        </button>
        <button data-testid="contacts-sort" onClick={() => toggleSort("last_activity_at")} style={btnGhost}>
          <ArrowUpDown size={13} /> Sort by activity
        </button>
        {/* Active filter chips */}
        {Object.entries(filters).filter(([_k, v]) => v).map(([k, v]) => (
          <span key={k} data-testid={`chip-${k}`} style={chipStyle}>
            {k}: <strong style={{ marginLeft: 4 }}>{v.length > 20 ? v.slice(0, 18) + "…" : v}</strong>
            <button onClick={() => setFilters((f) => ({ ...f, [k]: "" }))} style={chipClose}><X size={10} /></button>
          </span>
        ))}
        <div style={{ flex: 1 }} />
        <div style={{ position: "relative" }}>
          <Search size={14} color={ig.muted} style={{ position: "absolute", left: 11, top: 11 }} />
          <input data-testid="contacts-search" value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
            placeholder="Search Contacts (name, email, phone, business)" style={{
              width: 320, height: 34, paddingLeft: 32, paddingRight: 12, borderRadius: 8,
              border: `1px solid ${ig.border}`, background: ig.card, fontSize: 13, outline: "none", color: ig.ink,
            }} />
        </div>
        <button data-testid="manage-fields-open" style={btnGhost}><Cog size={13} /> Manage fields</button>
      </div>

      {/* Table */}
      <div style={{ padding: "16px 24px 0", overflowX: "auto" }}>
        <div style={{
          background: ig.card, border: `1px solid ${ig.border}`, borderRadius: 12,
          overflow: "hidden", boxShadow: "0 1px 2px rgba(7,31,24,0.03)",
        }}>
          <table data-testid="contacts-table" style={{ width: "100%", borderCollapse: "collapse", minWidth: 1240, fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#FAFCFB", borderBottom: `1px solid ${ig.border}` }}>
                <th style={{ ...th, width: 36, paddingLeft: 16 }}>
                  <Checkbox checked={allChecked} indeterminate={!allChecked && someChecked}
                    onChange={toggleAll} testId="contacts-check-all" />
                </th>
                <ThSort label="Contact name" k="name"           cur={sortKey} dir={sortDir} onClick={toggleSort} />
                <ThSort label="Phone"        k="phone"          cur={sortKey} dir={sortDir} onClick={toggleSort} />
                <ThSort label="Email"        k="email"          cur={sortKey} dir={sortDir} onClick={toggleSort} />
                <ThSort label="Business"     k="business"       cur={sortKey} dir={sortDir} onClick={toggleSort} />
                <ThSort label="Workspace"    k="workspace_id"   cur={sortKey} dir={sortDir} onClick={toggleSort} />
                <ThSort label="Owner"        k="owner_user_id"  cur={sortKey} dir={sortDir} onClick={toggleSort} />
                <ThSort label="Status"       k="status"         cur={sortKey} dir={sortDir} onClick={toggleSort} />
                <ThSort label="Created"      k="created_at"     cur={sortKey} dir={sortDir} onClick={toggleSort} />
                <ThSort label="Last activity" k="last_activity_at" cur={sortKey} dir={sortDir} onClick={toggleSort} />
                <th style={{ ...th, paddingRight: 16 }}>Tags</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} style={{ padding: 40, textAlign: "center", color: ig.muted }}>Loading…</td></tr>
              ) : error ? (
                <tr><td colSpan={11} style={{ padding: 40, textAlign: "center", color: "#B91C1C" }}>Error: {error}</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={11} style={{ padding: 40, textAlign: "center", color: ig.muted }}>No contacts match.</td></tr>
              ) : items.map((c) => (
                <Row key={c.id} c={c} selected={selected.has(c.id)} onToggle={() => toggleRow(c.id)} onOpen={() => nav(`/admin/contacts/${c.id}`)} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div style={{ padding: "14px 24px 24px", display: "flex", alignItems: "center", gap: 10 }}>
        <span data-testid="contacts-page-info" style={{ fontSize: 12.5, color: ig.muted }}>
          Page {page} of {pages} · {total} total
        </span>
        <div style={{ flex: 1 }} />
        <select data-testid="contacts-page-size" value={pageSize}
          onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} style={selectMini}>
          {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <button data-testid="contacts-prev" onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1} style={{ ...pagerBtn, opacity: page <= 1 ? 0.45 : 1 }}>
          <ChevronLeft size={13} /> Prev
        </button>
        <button data-testid="contacts-page-current" style={{ ...pagerBtn, background: "#DCFCE7", borderColor: "#86EFAC", color: "#15803D" }}>
          {page}
        </button>
        <button data-testid="contacts-next" onClick={() => setPage((p) => Math.min(pages, p + 1))}
          disabled={page >= pages} style={{ ...pagerBtn, opacity: page >= pages ? 0.45 : 1 }}>
          Next <ChevronRight size={13} />
        </button>
      </div>

      {showAdv && <AdvFilters value={filters} meta={filterMeta} onApply={(f) => { setFilters(f); setPage(1); setShowAdv(false); }} onClose={() => setShowAdv(false)} />}
      {showAdd && <AddContactModal meta={filterMeta} onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); }} defaultWorkspace={workspaceId} defaultAgency={agencyId} />}
      {showImport && <ImportModal meta={filterMeta} defaultWorkspace={workspaceId} onClose={() => setShowImport(false)} onImported={() => { setShowImport(false); load(); }} />}
      {showReassign && <ReassignModal ids={[...selected]} meta={filterMeta} onClose={() => setShowReassign(false)} onDone={() => { setShowReassign(false); setSelected(new Set()); load(); }} />}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */

function Row({ c, selected, onToggle, onOpen }) {
  const [hover, setHover] = useState(false);
  return (
    <tr data-testid={`contact-row-${c.id}`}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ borderBottom: `1px solid ${ig.borderSoft}`, background: hover ? "#F7FAF8" : ig.card, cursor: "pointer" }}>
      <td style={{ ...td, paddingLeft: 16 }} onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={selected} onChange={onToggle} testId={`contact-check-${c.id}`} />
      </td>
      <td style={td} onClick={onOpen}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar name={c.name} tone={c.tone} />
          <span style={{ fontWeight: 600, color: "#2563EB" }}>{c.name || <em style={{ color: ig.muted }}>Unnamed</em>}</span>
        </div>
      </td>
      <td style={td} onClick={onOpen}>{c.phone ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><PhoneIcon size={12} color={ig.muted} /> {c.phone}</span> : em()}</td>
      <td style={td} onClick={onOpen}>{c.email ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Mail size={12} color={ig.muted} /> {c.email}</span> : em()}</td>
      <td style={td} onClick={onOpen}>{c.business || em()}</td>
      <td style={td}>
        {c._workspace ? (
          <Link to={`/admin/workspaces/${c.workspace_id}/contacts`} onClick={(e) => e.stopPropagation()}
            style={pillLink}>{c._workspace.name}</Link>
        ) : em()}
      </td>
      <td style={td}>
        {c._owner ? (
          <Link to={`/admin/users/${c.owner_user_id}`} onClick={(e) => e.stopPropagation()}
            style={{ ...pillLink, background: "#EDE9FE", color: "#6D28D9" }}>{c._owner.name || c._owner.email}</Link>
        ) : em()}
      </td>
      <td style={td} onClick={onOpen}><StatusPill status={c.status} /></td>
      <td style={td} onClick={onOpen}><span style={{ color: ig.inkSoft }}>{fmt(c.created_at)}</span></td>
      <td style={td} onClick={onOpen}><span style={{ color: ig.inkSoft }}>{fmtAgo(c.last_activity_at)}</span></td>
      <td style={{ ...td, paddingRight: 16 }} onClick={onOpen}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, maxWidth: 220 }}>
          {(c.tags || []).slice(0, 3).map((t) => (
            <span key={t} title={t} style={tagPill}>{t.length > 20 ? t.slice(0, 20) + "…" : t}</span>
          ))}
          {(c.tags || []).length > 3 && <span style={{ ...tagPill, background: "#F1F5F4", color: ig.muted }}>+{c.tags.length - 3}</span>}
        </div>
      </td>
    </tr>
  );
}

function em() { return <span style={{ color: "#C7CFCB" }}>—</span>; }

function StatusPill({ status }) {
  const map = {
    active:       { bg: "#DCFCE7", fg: "#15803D" },
    inactive:     { bg: "#F1F5F4", fg: "#64748B" },
    unsubscribed: { bg: "#FEF3C7", fg: "#92400E" },
    bounced:      { bg: "#FECACA", fg: "#991B1B" },
    disabled:     { bg: "#FECACA", fg: "#991B1B" },
  };
  const p = map[status] || map.inactive;
  return <span style={{ background: p.bg, color: p.fg, padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700 }}>{status || "—"}</span>;
}

function ThSort({ label, k, cur, dir, onClick }) {
  const active = cur === k;
  return (
    <th data-testid={`th-${k}`} onClick={() => onClick(k)} style={{ ...th, cursor: "pointer", userSelect: "none" }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: active ? ig.ink : ig.muted, fontWeight: 700 }}>
        {label}
        {active ? (dir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />)
                : <ArrowUpDown size={11} color="#CBD5D0" />}
      </span>
    </th>
  );
}

function Avatar({ name, tone }) {
  const initials = (name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "?";
  const p = {
    blue:  { bg: "#DBEAFE", fg: "#1D4ED8" }, green: { bg: "#DCFCE7", fg: "#15803D" },
    pink:  { bg: "#FCE7F3", fg: "#BE185D" }, amber: { bg: "#FEF3C7", fg: "#A16207" },
    violet:{ bg: "#EDE9FE", fg: "#6D28D9" }, teal:  { bg: "#CCFBF1", fg: "#0F766E" },
    rose:  { bg: "#FFE4E6", fg: "#BE123C" }, sky:   { bg: "#E0F2FE", fg: "#0369A1" },
    slate: { bg: "#F1F5F4", fg: "#64748B" },
  }[tone || "slate"];
  return <span style={{ width: 28, height: 28, borderRadius: 999, background: p.bg, color: p.fg, display: "inline-grid", placeItems: "center", fontSize: 10.5, fontWeight: 800, flexShrink: 0 }}>{initials}</span>;
}
function Checkbox({ checked, indeterminate, onChange, testId }) {
  return <input type="checkbox" data-testid={testId} checked={!!checked} onChange={onChange}
    ref={(el) => { if (el) el.indeterminate = !!indeterminate && !checked; }}
    style={{ accentColor: "#22C55E", width: 14, height: 14, cursor: "pointer" }} />;
}

/* ── Advanced filters modal ──────────────────────────────────── */
function AdvFilters({ value, meta, onApply, onClose }) {
  const [local, setLocal] = useState({ ...value });
  const set = (k, v) => setLocal((s) => ({ ...s, [k]: v }));
  return (
    <div data-testid="adv-filters-modal" onClick={onClose} style={modalBackdrop}>
      <div onClick={(e) => e.stopPropagation()} style={modalPanel(600)}>
        <div style={modalHeader}><h2 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Advanced filters</h2><button onClick={onClose} style={iconBtn}><X size={13} /></button></div>
        <div style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, overflowY: "auto" }}>
          <Field label="Workspace">
            <select data-testid="filter-workspace" style={input} value={local.workspace_id} onChange={(e) => set("workspace_id", e.target.value)}>
              <option value="">Any workspace</option>
              {meta.workspaces?.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </Field>
          <Field label="Agency">
            <select data-testid="filter-agency" style={input} value={local.agency_id} onChange={(e) => set("agency_id", e.target.value)}>
              <option value="">Any agency</option>
              {meta.agencies?.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.tier})</option>)}
            </select>
          </Field>
          <Field label="Account owner">
            <select data-testid="filter-owner" style={input} value={local.owner_user_id} onChange={(e) => set("owner_user_id", e.target.value)}>
              <option value="">Any owner</option>
              {meta.owners?.map((o) => <option key={o.id} value={o.id}>{o.name || o.email}</option>)}
            </select>
          </Field>
          <Field label="Plan">
            <select data-testid="filter-plan" style={input} value={local.plan} onChange={(e) => set("plan", e.target.value)}>
              <option value="">Any plan</option>
              {meta.plans?.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select data-testid="filter-status" style={input} value={local.status} onChange={(e) => set("status", e.target.value)}>
              <option value="">Any status</option>
              {meta.statuses?.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Source">
            <select data-testid="filter-source" style={input} value={local.source} onChange={(e) => set("source", e.target.value)}>
              <option value="">Any source</option>
              {meta.sources?.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Tag">
            <select data-testid="filter-tag" style={input} value={local.tag} onChange={(e) => set("tag", e.target.value)}>
              <option value="">Any tag</option>
              {meta.tags?.map((t) => <option key={t.tag} value={t.tag}>{t.tag} ({t.count})</option>)}
            </select>
          </Field>
          <div />
          <Field label="Created from"><input type="date" style={input} value={local.created_from} onChange={(e) => set("created_from", e.target.value)} data-testid="filter-created-from" /></Field>
          <Field label="Created to"><input type="date" style={input} value={local.created_to} onChange={(e) => set("created_to", e.target.value)} data-testid="filter-created-to" /></Field>
        </div>
        <div style={modalFooter}>
          <button data-testid="adv-filters-clear" onClick={() => setLocal({ workspace_id: "", agency_id: "", owner_user_id: "", status: "", source: "", tag: "", plan: "", created_from: "", created_to: "" })} style={btnSecondary}>Clear all</button>
          <div style={{ flex: 1 }} />
          <button data-testid="adv-filters-cancel" onClick={onClose} style={btnSecondary}>Cancel</button>
          <button data-testid="adv-filters-apply" onClick={() => onApply(local)} style={btnPrimaryGreen}>Apply</button>
        </div>
      </div>
    </div>
  );
}

/* ── Import & Reassign modals ─────────────────────────────────── */
function ImportModal({ meta, defaultWorkspace, onClose, onImported }) {
  const [wid, setWid] = useState(defaultWorkspace || "");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState(null);
  const submit = async () => {
    if (!wid || !file) { setMsg({ err: "Pick workspace + CSV file" }); return; }
    setBusy(true); setMsg(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contacts/import?workspace_id=${wid}`,
        { method: "POST", headers: { Authorization: `Bearer ${api.token()}` }, body: fd });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || "Import failed");
      setMsg({ ok: `Imported ${j.inserted} contacts.` });
      setTimeout(onImported, 800);
    } catch (e) { setMsg({ err: e.message }); } finally { setBusy(false); }
  };
  return (
    <div data-testid="import-modal" onClick={onClose} style={modalBackdrop}>
      <div onClick={(e) => e.stopPropagation()} style={modalPanel(480)}>
        <div style={modalHeader}><h2 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Import contacts (CSV)</h2><button onClick={onClose} style={iconBtn}><X size={13} /></button></div>
        <div style={{ padding: 20 }}>
          <Field label="Destination workspace">
            <select style={input} value={wid} onChange={(e) => setWid(e.target.value)} data-testid="import-workspace">
              <option value="">Select workspace</option>
              {meta.workspaces?.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </Field>
          <Field label="CSV file (columns: name, first_name, last_name, email, phone, business, status, tags)">
            <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-file" />
          </Field>
          {msg?.err && <div style={{ color: "#B91C1C", fontSize: 12, marginTop: 6 }}>{msg.err}</div>}
          {msg?.ok  && <div style={{ color: "#15803D", fontSize: 12, marginTop: 6 }}>{msg.ok}</div>}
        </div>
        <div style={modalFooter}>
          <div style={{ flex: 1 }} />
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimaryGreen} onClick={submit} disabled={busy} data-testid="import-submit">
            {busy ? "Importing…" : "Import"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReassignModal({ ids, meta, onClose, onDone }) {
  const [wid, setWid] = useState(""); const [oid, setOid] = useState("");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(null);
  const submit = async () => {
    if (!wid && !oid) { setErr("Pick a target workspace or owner"); return; }
    setBusy(true); setErr(null);
    try {
      await api.post("/api/admin/contacts/reassign", { ids, workspace_id: wid || undefined, owner_user_id: oid || undefined });
      onDone();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <div data-testid="reassign-modal" onClick={onClose} style={modalBackdrop}>
      <div onClick={(e) => e.stopPropagation()} style={modalPanel(460)}>
        <div style={modalHeader}><h2 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Reassign {ids.length} contact(s)</h2><button onClick={onClose} style={iconBtn}><X size={13} /></button></div>
        <div style={{ padding: 20 }}>
          <Field label="Target workspace (optional)">
            <select style={input} value={wid} onChange={(e) => setWid(e.target.value)} data-testid="reassign-workspace">
              <option value="">Keep current</option>
              {meta.workspaces?.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </Field>
          <Field label="New owner (optional)">
            <select style={input} value={oid} onChange={(e) => setOid(e.target.value)} data-testid="reassign-owner">
              <option value="">Keep current</option>
              {meta.owners?.map((o) => <option key={o.id} value={o.id}>{o.name || o.email}</option>)}
            </select>
          </Field>
          {err && <div style={{ color: "#B91C1C", fontSize: 12, marginTop: 6 }}>{err}</div>}
        </div>
        <div style={modalFooter}>
          <div style={{ flex: 1 }} />
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimaryGreen} onClick={submit} disabled={busy} data-testid="reassign-submit">
            {busy ? "Saving…" : "Reassign"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11.5, color: ig.ink, fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
function fmtAgo(iso) {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return fmt(iso);
}

/* Styles */
const th = { textAlign: "left", padding: "12px 14px", fontSize: 11.5, fontWeight: 700, color: ig.muted, borderBottom: `1px solid ${ig.border}`, whiteSpace: "nowrap" };
const td = { padding: "12px 14px", color: ig.ink, fontSize: 12.8, verticalAlign: "middle", whiteSpace: "nowrap" };
const btnPrimaryGreen = { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: 0, background: "linear-gradient(135deg, #18B86B, #22C55E)", color: "#fff", fontWeight: 700, fontSize: 12.5, cursor: "pointer", boxShadow: "0 4px 12px rgba(24,184,107,0.22)" };
const btnSecondary    = { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: `1px solid ${ig.border}`, background: ig.card, color: ig.ink, fontWeight: 700, fontSize: 12.5, cursor: "pointer" };
const btnGhost        = { display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 8, border: `1px solid ${ig.border}`, background: ig.card, color: ig.ink, fontWeight: 600, fontSize: 12.5, cursor: "pointer" };
const iconBtn         = { width: 34, height: 34, borderRadius: 8, border: `1px solid ${ig.border}`, background: ig.card, cursor: "pointer", display: "inline-grid", placeItems: "center", color: ig.ink };
const pagerBtn        = { display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 11px", borderRadius: 7, border: `1px solid ${ig.border}`, background: ig.card, color: ig.ink, fontSize: 12.5, fontWeight: 700, cursor: "pointer", minWidth: 32, justifyContent: "center" };
const selectMini      = { height: 30, padding: "0 6px", borderRadius: 7, border: `1px solid ${ig.border}`, background: ig.card, fontSize: 12.5, outline: "none" };
const input           = { width: "100%", height: 36, padding: "0 11px", borderRadius: 8, border: `1px solid ${ig.border}`, background: ig.card, fontSize: 13, outline: "none", color: ig.ink };
const chipStyle       = { display: "inline-flex", alignItems: "center", gap: 4, background: "#F1F5F4", color: ig.ink, padding: "4px 4px 4px 10px", borderRadius: 999, fontSize: 11.5, fontWeight: 600 };
const chipClose       = { width: 18, height: 18, borderRadius: 999, border: 0, background: "transparent", cursor: "pointer", display: "inline-grid", placeItems: "center", color: ig.muted };
const tagPill         = { background: "#EEF3F0", color: ig.inkSoft, fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const pillLink        = { background: "#DBEAFE", color: "#1D4ED8", fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, textDecoration: "none" };
const pillBtn         = { marginLeft: 10, padding: "2px 8px", borderRadius: 6, border: `1px solid ${ig.border}`, background: ig.card, fontSize: 11, cursor: "pointer" };
const moreMenu        = { position: "absolute", top: 68, right: 24, background: ig.card, border: `1px solid ${ig.border}`, borderRadius: 10, boxShadow: ig.shadowLg, minWidth: 240, padding: 6, zIndex: 40, display: "flex", flexDirection: "column", gap: 2 };
const menuItem        = { display: "inline-flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", border: 0, background: "transparent", textAlign: "left", cursor: "pointer", fontSize: 13, color: ig.ink, borderRadius: 7 };
const modalBackdrop   = { position: "fixed", inset: 0, background: "rgba(7,31,24,0.42)", zIndex: 100000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 };
const modalPanel      = (w) => ({ background: ig.card, width: w, maxWidth: "92vw", maxHeight: "86vh", borderRadius: 14, boxShadow: ig.shadowLg, display: "flex", flexDirection: "column", overflow: "hidden" });
const modalHeader     = { padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${ig.border}` };
const modalFooter     = { padding: "12px 20px", borderTop: `1px solid ${ig.border}`, display: "flex", alignItems: "center", gap: 8 };
